import { PreApplicationRecommendation } from '../applications/pre-application.types';

/** Response shape of GET /api/ops/queue-summary/ — the Ops dashboard payload. */
export interface OpsQueueSummary {
  window_days: number;
  generated_at: string;
  sla_business_hours: number;
  metrics: OpsQueueMetrics;
  queues: OpsQueues;
  /** AI recommendation mix across KS + DF facilities in review. */
  recommendations: Partial<Record<OpsAiRecommendation, number>>;
  intake: OpsIntake;
  /** Combined KS + DF application-stage mix (drives the stages donut). */
  status_breakdown: OpsStatusBreakdown;
  /** Newest applications across both products, with submitter + deep link. */
  recent_applications: OpsRecentApplication[];
  overdue: OpsOverdueItem[];
  /** Pre-applications awaiting a decision, longest-waiting first. */
  pre_applications_waiting: OpsPreApplicationItem[];
  /** Pipeline funnel: ordered stages with actor routing and counts. */
  pipeline: OpsPipeline;
  /** The stage currently taking the longest — null when no items in flight. */
  slowest_stage: OpsSlowestStage | null;
  /** Number of items currently assigned to the requesting user. */
  mine_count: number;
  /** Number of items with no assignee. */
  unassigned_count: number;
  /** Ops tier of the requesting user — null for non-ops staff. */
  current_user_tier: 'ANALYST' | 'SENIOR_OPS' | 'CREDIT_COMMITTEE' | null;
  /** Pre-sorted worklist (~12 items): OPS first, then CC, then CLIENT; DECLINED excluded. */
  worklist: OpsWorklistItem[];
}

/** Shared fields added to application items in v2 of the payload. */
interface OpsReviewFields {
  review_stage: OpsReviewStage;
  review_stage_label: string;
  next_actor: NextActor;
  next_step_label: string;
  requires_cc: boolean;
  /** Display name of the assignee, or '' when unassigned. */
  assignee: string;
  assignee_id: number | null;
  assignee_is_me: boolean;
  hours_in_stage: number;
  can_pick_up: boolean;
}

type OpsReviewStage =
  | 'AWAITING_PICKUP'
  | 'IN_REVIEW'
  | 'AWAITING_INFO'
  | 'READY_FOR_DECISION'
  | 'AWAITING_CC'
  | 'DECLINED';

export type NextActor = 'OPS' | 'CLIENT' | 'CREDIT_COMMITTEE';

/** Worklist wording and pipeline-segment modifier for whoever acts next. */
export const NEXT_ACTOR_META: Record<NextActor, { label: string; cssClass: string }> = {
  OPS: { label: 'You', cssClass: 'actor--ops' },
  CLIENT: { label: 'Waiting on client', cssClass: 'actor--client' },
  CREDIT_COMMITTEE: { label: 'Credit Committee', cssClass: 'actor--cc' },
};

interface OpsPipeline {
  stages: OpsPipelineStage[];
  total: number;
}

export interface OpsPipelineStage {
  key: string;
  label: string;
  count: number;
  actor: NextActor;
}

interface OpsSlowestStage {
  key: string;
  label: string;
  hours: number;
}

export interface OpsWorklistItem extends OpsReviewFields {
  product: 'KS' | 'DF';
  short_id: string;
  entity_name: string;
  amount: string;
  ai_recommendation: string;
  created_at: string;
  url: string;
}

export interface OpsStatusBreakdown {
  ks_stage: Record<string, number>;
  df_stage: Record<string, number>;
}

export interface OpsRecentApplication extends OpsReviewFields {
  product: 'KS' | 'DF';
  short_id: string;
  entity_name: string;
  /** Full name (or email) of the user who submitted the application. */
  applicant: string;
  amount: string;
  stage: string;
  ai_recommendation: string;
  created_at: string;
  /** Deep link to the item's Ops detail page. */
  url: string;
}

interface OpsQueueMetrics {
  total_waiting: number;
  total_breaching: number;
}

/** A pre-application awaiting a decision. No amount and no SLA (ruling Q4). */
export interface OpsPreApplicationItem {
  short_id: string;
  entity_name: string;
  /** Full name (or email) of the user who started the pre-application. */
  submitted_by: string;
  recommendation: PreApplicationRecommendation;
  /** Display name of who is dealing with it, or '' when nobody is. */
  assignee: string;
  submitted_at: string | null;
  /** Deep link to the item's Ops detail page. */
  url: string;
}

interface OpsQueues {
  pre_applications: OpsQueue;
  ks: OpsQueue;
  df_facilities: OpsQueue;
  df_invoices: OpsQueue;
}

type OpsQueueKey = keyof OpsQueues;

export interface OpsQueue {
  label: string;
  waiting: number;
  breaching: number;
  url: string;
}

export interface OpsIntake {
  days: string[];
  ks: number[];
  df: number[];
}

export type OpsAiRecommendation = 'APPROVE' | 'MORE_INFO' | 'DECLINE';

export interface OpsOverdueItem extends OpsReviewFields {
  product: 'KS' | 'DF';
  queue: OpsQueueKey;
  short_id: string;
  entity_name: string;
  amount: string;
  stage: string;
  ai_recommendation: string;
  hours_overdue: number;
  submitted_at: string;
  /** Deep link to the item's Ops detail page. */
  url: string;
}

/** Response from POST /api/ops/pick-up/ */
export interface OpsPickUpResponse {
  assignee: string;
  assignee_id: number;
  assignee_is_me: true;
}

/** Response shape of GET /api/ops/breaching/ — the SLA breach drill-down. */
export interface OpsBreachingResponse {
  items: OpsOverdueItem[];
  count: number;
}
